// dSpr Cycle Scheduler - `/dSpr_CycleScheduler`
//
// HR-only page (Admin / Sub Admin) that lists every half-yearly self-review
// cycle, lets you create a new one, and exposes the per-manager bulk-mail
// batch buttons + the cycle's Email Trigger History timeline.
//
// Sibling page: dSpr_ReviewManagement (single-employee row view).
// Backend:      /api/dspr/cycles + /api/dspr/reviews
//
// Notes for future readers:
//   • Every email sent by this page lands a row in dspr_email_logs. The Email
//     Trigger History timeline aggregates from there.
//   • manager_id on dspr_reviews is NOT NULL; unallocated employees never enter
//     a cycle. See src/utils/dSpr_AutoBackfill.js for the allocation-sync rules.
//   • R1 is required on a cycle; R2 + R3 are optional.
//   • "Pending" in the card header is a derived count = Initiated + In Progress;
//     it is NOT a persisted status.
import React, { useEffect, useMemo, useState } from "react";
// Swal removed - themed alerts via useDsprAlerts() (see utils/dSprAlerts.js).
import { useNavigate } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { profileColorFromName } from "./utils/profileColor";
import { useDsprAlerts } from "./utils/dSprAlerts";
import {
    fmtDate, fmtDateTimeIST, fmtDateTimeShort, fmtDateLong,
} from "./utils/dSpr_DateFormat";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";

// Status of the cycle itself (Scheduled / Active / Closed)
// Cycle status pill colors - per dEpr's spec:
//   Scheduled : neutral grey + muted (calm; nothing happening yet)
//   Active    : green with subtle glow (the cycle HR cares about right now)
//   Closed    : dark grey + slightly opaque (archived; out of mind)
const STATUS_COLORS = {
    Scheduled: { bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8", glow: false },
    Active:    { bg: "#D1FAE5", fg: "#065F46", dot: "#10B981", glow: true  },
    Closed:    { bg: "#E5E7EB", fg: "#374151", dot: "#6B7280", glow: false, dim: true },
};

// Per-review status pill (employee chips inside ManagerBreakdown)
const REVIEW_STATUS_COLORS = {
    "Not Started": { bg: "#f8fafc", fg: "#94a3b8" },
    "Initiated":   { bg: "#eef2ff", fg: "#4338ca" },
    "In Progress": { bg: "#fef3c7", fg: "#92400e" },
    "Submitted":   { bg: "#d1fae5", fg: "#065f46" },
    "Approved":    { bg: "#dcfce7", fg: "#14532d" },
};

// Email-type → display label + dot colour for the Email Trigger History timeline.
// Order is intentionally fixed: request → reminder1/2 → confirmation, regardless
// of timestamps (so a delayed-arriving request still anchors the top of the list).
const EMAIL_TYPE_META = {
    request: { label: "Review request", dot: "#10b981", notesFn: (n) => `Initial trigger - covers ${n} employee${n !== 1 ? "s" : ""}` },
    reminder1: { label: "Reminder 1", dot: "#f59e0b", notesFn: (n) => `Sent to ${n} employee${n !== 1 ? "s" : ""}` },
    reminder2: { label: "Reminder 2", dot: "#8b5cf6", notesFn: (n) => `Sent to ${n} employee${n !== 1 ? "s" : ""}` },
    confirmation: { label: "Submission confirm", dot: "#3b82f6", notesFn: (n) => `${n} review${n !== 1 ? "s" : ""} confirmed` },
};
const EMAIL_TYPE_ORDER = ["request", "reminder1", "reminder2", "confirmation"];

// Formatters live in src/utils/dSpr_DateFormat.js - imported above.
// Defensive guard - anything that should be an array but might be an error
// object / undefined / null becomes [].
function safeArr(maybe) {
    if (Array.isArray(maybe)) return maybe;
    if (maybe && Array.isArray(maybe.rows)) return maybe.rows;
    if (maybe && Array.isArray(maybe.reviews)) return maybe.reviews;
    return [];
}

function DSprCycleScheduler({ navSize }) {
    const navigate = useNavigate();
    // Themed alerts - replaces Swal across the dSpr pages.
    const { showAlert, showConfirm, AlertsHost } = useDsprAlerts();
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewModal, setShowNewModal] = useState(false);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    // Per-cycle expansion state. Two independent sections per cycle:
    //   expanded[cycleId] = { breakdown: bool, history: bool }
    const [expanded, setExpanded] = useState({});
    const [breakdown, setBreakdown] = useState({}); // cycleId → { reviews: [] }
    const [busy, setBusy] = useState(null);

    // ── Initial load: confirm HR access + fetch cycles ──────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch { navigate("/login"); return; }
            await fetchCycles();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCycles = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dspr/cycles", { credentials: "include" });
            const list = await res.json();
            setCycles(safeArr(list));
        } catch (e) {
            showAlert("error", "Couldn't load cycles", e.message);
            setCycles([]);
        } finally {
            setLoading(false);
        }
    };

    // ── Activate-button eligibility (mirrors the server's RULES 1 + 3) ──────
    //
    // The server is the authority - these checks are a UX layer to disable
    // the button + show the reason BEFORE the user clicks. Avoids the
    // round-trip popup the screenshot exposed.
    //
    //   RULE 1 - if 2 cycles already Active, no Scheduled cycle can activate.
    //   RULE 3 - the SAME-HALF prior-year cycle (H1 2027 → H1 2026; H2 2027
    //              → H2 2026) must be Active or Closed. For half-yearly
    //              cycles this matches HR's mental model better than strict
    //              chronological order - H1 and H2 of the same year are
    //              parallel halves, not a sequence.
    //
    // Returns a Map<cycleId, blockerMessage | null>. null = clickable, string
    // = disabled with that reason shown as a tooltip + caption.
    const activateEligibility = useMemo(() => {
        const activeCount = cycles.filter(c => c.status === "Active").length;
        // Index all cycles by (half_num, year) for the predecessor lookup.
        const byHalfYear = new Map();
        for (const c of cycles) byHalfYear.set(`${c.half_num}:${c.year}`, c);
        const blockers = new Map();
        for (const c of cycles) {
            if (c.status !== "Scheduled") continue;
            // RULE 3 - same half, immediately prior year that exists.
            // Walk back year by year until we either find one or run out.
            let pred = null;
            for (let y = c.year - 1; y >= c.year - 10; y--) {
                const candidate = byHalfYear.get(`${c.half_num}:${y}`);
                if (candidate) { pred = candidate; break; }
            }
            if (pred && pred.status === "Scheduled") {
                blockers.set(c.id, `Cannot activate yet - ${pred.half_label} must be Active or Closed first.`);
                continue;
            }
            // RULE 1 - global Active cap.
            if (activeCount >= 2) {
                blockers.set(c.id, "2 cycles are already active. Close one before activating another.");
                continue;
            }
            blockers.set(c.id, null);
        }
        return blockers;
    }, [cycles]);

    // ── Stats row (4 cards): Total / Scheduled / Active / Closed ────────────
    const stats = useMemo(() => {
        const acc = { total: cycles.length, scheduled: 0, active: 0, closed: 0 };
        cycles.forEach(c => {
            if (c.status === "Scheduled") acc.scheduled++;
            else if (c.status === "Active") acc.active++;
            else if (c.status === "Closed") acc.closed++;
        });
        return acc;
    }, [cycles]);

    // ── Year picker (placeholder - only current year for now) ───────────────
    const currentYear = new Date().getFullYear();

    // ── Per-cycle expand toggles. Section = 'breakdown' | 'history'. ────────
    // Independent: opening one doesn't close the other.
    const toggleSection = async (cycleId, section) => {
        setExpanded(prev => {
            const cur = prev[cycleId] || { breakdown: false, history: false };
            return { ...prev, [cycleId]: { ...cur, [section]: !cur[section] } };
        });
        // Lazy fetch breakdown reviews on first open
        if (section === "breakdown") {
            const cur = expanded[cycleId] || { breakdown: false };
            if (!cur.breakdown && !breakdown[cycleId]) {
                try {
                    const res = await apiFetch(`/api/dspr/reviews/by-cycle/${cycleId}`, { credentials: "include" });
                    const list = await res.json();
                    setBreakdown(prev => ({ ...prev, [cycleId]: { reviews: safeArr(list) } }));
                } catch (e) {
                    showAlert("error", "Couldn't load breakdown", e.message);
                    setBreakdown(prev => ({ ...prev, [cycleId]: { reviews: [] } }));
                }
            }
        }
    };

    // After a send, invalidate cached breakdown for that cycle + refetch cycles
    // (so emailHistory / has_fired flags refresh) + refetch the breakdown.
    const refreshAfterSend = async (cycleId) => {
        await fetchCycles();
        try {
            const res = await apiFetch(`/api/dspr/reviews/by-cycle/${cycleId}`, { credentials: "include" });
            const list = await res.json();
            setBreakdown(prev => ({ ...prev, [cycleId]: { reviews: safeArr(list) } }));
        } catch { /* tolerated */ }
    };

    // ── Status transitions (activate / close) ───────────────────────────────
    //
    // Surfaces the SERVER's exact rejection message in the error toast so HR
    // sees the specific rule they tripped (e.g. "2 cycles are already
    // active. Close one before activating another." or "Cannot activate
    // H2 2026 - H1 2026 must be Active or Closed first."). Don't substitute
    // a generic "Failed to activate" - the specific message tells HR what
    // to fix.
    const transitionCycle = async (cycleId, action, confirmText) => {
        if (confirmText) {
            const titleByAction = {
                activate: "Activate this cycle?",
                close:    "Close this cycle?",
                reopen:   "Reopen this cycle?",
            };
            const labelByAction = {
                activate: "Yes, proceed",
                close:    "Close Cycle",
                reopen:   "Reopen Cycle",
            };
            const ok = await showConfirm({
                title: titleByAction[action] || "Confirm",
                body: confirmText,
                confirmLabel: labelByAction[action] || "Yes, proceed",
                danger: action === "close",
            });
            if (!ok) return;
        }
        setBusy(`${action}…`);
        try {
            const res = await apiFetch(`/api/dspr/cycles/${cycleId}/${action}`, {
                method: "POST", credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || data.message || "Action failed");
            }
            await fetchCycles();
            // Activation seeds dspr_reviews rows server-side (Rule 6). If the
            // user had the breakdown panel already opened pre-activation, the
            // cached reviews array is stale (it was [] because the cycle was
            // Scheduled). Invalidate AND refetch so the new employee tiles
            // surface without requiring a page reload. Close also fires this
            // to keep statuses + email_logs current.
            setBreakdown(prev => {
                const next = { ...prev };
                delete next[cycleId];
                return next;
            });
            try {
                const res2 = await apiFetch(`/api/dspr/reviews/by-cycle/${cycleId}`, { credentials: "include" });
                const list = await res2.json();
                setBreakdown(prev => ({ ...prev, [cycleId]: { reviews: safeArr(list) } }));
            } catch { /* tolerated - breakdown will lazy-fetch on next expand */ }

            const seeded = action === "activate" && typeof data.reviews_created === "number"
                ? `${data.reviews_created} review${data.reviews_created === 1 ? "" : "s"} seeded.`
                : "";
            showAlert("success", `Cycle ${data.new_status}`, seeded || undefined);
        } catch (e) {
            showAlert("error", "Action failed", e.message);
        } finally {
            setBusy(null);
        }
    };

    // ── Sync employees into an already-Active cycle ─────────────────────────
    //
    // Activation is the ONLY point at which dspr_reviews gets seeded, so anyone
    // hired - or finally given a reporting manager - after that moment never
    // appears in the cycle. This preflights with /sync-preview so HR sees
    // exactly who will be pulled in (and who can't be, because Manager
    // Allocation hasn't given them a manager) before committing.
    //
    // The POST is idempotent server-side, so a double-click is harmless.
    const syncCycleEmployees = async (cycleId, halfLabel) => {
        setBusy("Checking for new employees…");
        let preview;
        try {
            const res = await apiFetch(`/api/dspr/cycles/${cycleId}/sync-preview`, { credentials: "include" });
            preview = await res.json();
            if (!res.ok) throw new Error(preview.error || "Could not check for new employees");
        } catch (e) {
            showAlert("error", "Sync check failed", e.message);
            return;
        } finally {
            setBusy(null);
        }

        const toAdd   = safeArr(preview.would_add);
        const skipped = safeArr(preview.skipped_no_manager);

        // Surfacing this every time is the point: an employee with no manager is
        // invisible to dSpr forever, and the only place to fix it is Manager
        // Allocation. Silent skipping is how people lose a whole review cycle.
        const skippedNote = skipped.length
            ? ` ${skipped.length} active employee${skipped.length === 1 ? " has" : "s have"} no reporting manager and cannot be added - assign one in Manager Allocation first.`
            : "";

        if (toAdd.length === 0) {
            showAlert("info", "Nothing to sync",
                `Every eligible employee is already in ${halfLabel}.${skippedNote}`);
            return;
        }

        const names = toAdd.slice(0, 8).map(a => a.emp_name).join(", ");
        const more  = toAdd.length > 8 ? `, and ${toAdd.length - 8} more` : "";
        const ok = await showConfirm({
            title: `Add ${toAdd.length} employee${toAdd.length === 1 ? "" : "s"} to ${halfLabel}?`,
            body: `${names}${more}. They'll be seeded as "Not Started" - you still need to send them a Request Review email.${skippedNote}`,
            confirmLabel: "Sync Employees",
        });
        if (!ok) return;

        setBusy("Syncing…");
        try {
            const res = await apiFetch(`/api/dspr/cycles/${cycleId}/sync-employees`, {
                method: "POST", credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || data.message || "Sync failed");

            await fetchCycles();
            // Same cache-invalidation dance as transitionCycle: the breakdown
            // panel holds a stale reviews array that predates the new rows.
            setBreakdown(prev => {
                const next = { ...prev };
                delete next[cycleId];
                return next;
            });
            try {
                const res2 = await apiFetch(`/api/dspr/reviews/by-cycle/${cycleId}`, { credentials: "include" });
                const list = await res2.json();
                setBreakdown(prev => ({ ...prev, [cycleId]: { reviews: safeArr(list) } }));
            } catch { /* tolerated - breakdown will lazy-fetch on next expand */ }

            const added = safeArr(data.added).map(a => a.emp_name).join(", ");
            showAlert(
                "success",
                `${data.reviews_created} employee${data.reviews_created === 1 ? "" : "s"} added to ${halfLabel}`,
                added || undefined
            );
        } catch (e) {
            showAlert("error", "Sync failed", e.message);
        } finally {
            setBusy(null);
        }
    };

    // ── Per-manager bulk send ───────────────────────────────────────────────
    //
    // Split into two functions:
    //   doSendByManager       - performs the POST, shows the result Swal,
    //                            refreshes the cycle. No confirmation prompt.
    //   sendByManager         - Swal-confirm wrapper around doSendByManager,
    //                            used by the standalone Request Review / Send
    //                            Reminder buttons in the manager block.
    //
    // The BatchPreviewModal calls doSendByManager directly because the preview
    // itself already serves as the confirmation step - a second Swal would be
    // redundant friction.
    const doSendByManager = async (cycleId, managerId, act, label) => {
        setBusy(`Sending ${label}…`);
        try {
            const res = await apiFetch(
                `/api/dspr/reviews/bulk/${cycleId}/by-manager/${encodeURIComponent(managerId)}/${act}`,
                { method: "POST", credentials: "include" }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Send failed");
            await refreshAfterSend(cycleId);
            // Build a compact failure summary as plain text (banner doesn't
            // render HTML - it renders a clean title + message).
            const failureLine = data.failures && data.failures.length
                ? `${data.failures.length} skipped: ${data.failures.slice(0, 3).map(f => f.name).join(", ")}${data.failures.length > 3 ? ` (+${data.failures.length - 3} more)` : ""}`
                : `${data.sent} ${label}${data.sent !== 1 ? "s" : ""} delivered.`;
            showAlert(
                data.sent === data.attempted ? "success" : "warning",
                `${data.sent} / ${data.attempted} sent`,
                failureLine,
            );
            return data;
        } catch (e) {
            showAlert("error", "Send failed", e.message);
            throw e;
        } finally {
            setBusy(null);
        }
    };

    const sendByManager = async (cycleId, managerId, act, label) => {
        const ok = await showConfirm({
            title: `Send ${label}?`,
            body: "This sends to every employee under this manager (status-permitting). Each employee receives their own email with their own self-review link.",
            confirmLabel: "Send",
        });
        if (!ok) return;
        try { await doSendByManager(cycleId, managerId, act, label); } catch { /* already toasted */ }
    };

    return (
        <div className="dspr-container">
            {busy && (
                <div className="ec-loader-overlay">
                    <div className="ec-loader-spinner" />
                    <div className="ec-loader-text">{busy}</div>
                </div>
            )}
            <div className={`dspr-dashboard ${navSize}`}>
                {/* Themed banner + confirm dialog - mounted INSIDE the dashboard
                    so the banner participates in the dashboard's block layout
                    instead of becoming a flex-row item next to it (which caused
                    a visible horizontal shift when the banner appeared). */}
                <AlertsHost />
                {/* Header */}
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        SPR - Cycle Scheduler
                    </h2>
                    <p className="dspr-subtitle">Plan half-yearly self-review cycles. Each cycle records every allocated employee's reporting manager at create time, then per-manager bulk-mail batches go out once activated.</p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* Stats row (4 cards) - unchanged */}
                    <div className="dspr-stats">
                        <div className="dspr-stat dspr-stat-blue"><div className="dspr-stat-num">{stats.total}</div><div className="dspr-stat-label">Total Cycles</div></div>
                        <div className="dspr-stat dspr-stat-indigo"><div className="dspr-stat-num">{stats.scheduled}</div><div className="dspr-stat-label">Scheduled</div><div className="dspr-stat-hint">Not yet started</div></div>
                        <div className="dspr-stat dspr-stat-green"><div className="dspr-stat-num">{stats.active}</div><div className="dspr-stat-label">Active</div><div className="dspr-stat-hint">Accepting submissions</div></div>
                        <div className="dspr-stat dspr-stat-grey"><div className="dspr-stat-num">{stats.closed}</div><div className="dspr-stat-label">Closed</div><div className="dspr-stat-hint">Archived</div></div>
                    </div>

                    {/* Year picker row - current year as a CURRENT pill + Create button on the right. */}
                    {/* Multi-year support is a later enhancement. */}
                    <div className="dspr-year-row">
                        <div className="dspr-year-picker">
                            <span className="dspr-year-label">Year:</span>
                            <span className="dspr-year-pill">{currentYear} <span className="dspr-year-tag">CURRENT</span></span>
                        </div>
                        <div className="dspr-year-actions">
                            <button className="dspr-btn-options" onClick={() => setShowOptionsModal(true)}
                                title="Manage Client / Project dropdown values">
                                ⚙ Client List
                            </button>
                            <button className="dspr-btn-new" onClick={() => setShowNewModal(true)}>+ Create New Cycle</button>
                        </div>
                    </div>

                    {/* Cycle list */}
                    {loading ? (
                        <div className="dspr-empty">Loading cycles…</div>
                    ) : cycles.length === 0 ? (
                        <div className="dspr-empty">No cycles yet. Click <strong>+ Create New Cycle</strong> above to create the first one.</div>
                    ) : cycles.map(c => (
                        <CycleCard
                            key={c.id}
                            cycle={c}
                            expanded={expanded[c.id] || { breakdown: false, history: false }}
                            breakdown={breakdown[c.id]}
                            onToggleSection={toggleSection}
                            onTransition={transitionCycle}
                            onSync={syncCycleEmployees}
                            onSend={sendByManager}
                            onDirectSend={(b) => doSendByManager(b.cycleId, b.managerId, b.act, b.label)}
                            onAfterEdit={fetchCycles}
                            navigate={navigate}
                            activateBlockedReason={activateEligibility.get(c.id) || null}
                        />
                    ))}
                </div>
            </div>

            {showNewModal && (
                <NewCycleModal
                    existingCycles={cycles}
                    onClose={() => setShowNewModal(false)}
                    onCreated={async () => { setShowNewModal(false); await fetchCycles(); }}
                />
            )}

            {showOptionsModal && (
                <ClientProjectOptionsModal onClose={() => setShowOptionsModal(false)} />
            )}
        </div>
    );
}

// ── Cycle card - single horizontal dates row + inline edit mode ────────────
//
//  Read mode:
//   ┌──────────────────────────────────────────────────────────────────────┐
//   │ [H1 badge]  H1 2026  ● Active   3 Not Started · 0 Pending · 0 Submitted│
//   │             Start: 2026-01-01  End: 2026-06-30  R1: <date>  R2: ...   │
//   │                                                          0/3          │
//   │                                                       Submitted       │
//   │                                                     [progress bar]    │
//   │                                              [Edit Dates] [Close]     │
//   ├──────────────────────────────────────────────────────────────────────┤
//   │ Manager & Employee Breakdown ▼   │   Email Trigger History ▼          │
//   └──────────────────────────────────────────────────────────────────────┘
//
//  Edit mode (Start/End always locked; R1/R2/R3 locked if fired):
//   The dates row converts to per-slot inputs with small uppercase labels.
//   Fired reminders show a green "REMINDER N · SENT" label and a disabled,
//   green-tinted input. Non-fired R2/R3 inputs get an × button to remove.
//   "+ Reminder" appears at the end of the row if reminders.length < 3.
//   Save/Cancel buttons replace Edit Dates/Close Cycle in the action column.
function CycleCard({ cycle: c, expanded, breakdown, onToggleSection, onTransition, onSync, onSend, onDirectSend, onAfterEdit, navigate, activateBlockedReason }) {
    const sc = STATUS_COLORS[c.status] || STATUS_COLORS.Closed;
    // Themed alerts scoped to this card (its own state, own host).
    const { showAlert, AlertsHost } = useDsprAlerts();

    // Counts (the breakdown line on the title row)
    const total = c.total_reviews || 0;
    const notStarted = c.not_started_count || 0;
    const pending = c.pending_count || 0; // Initiated + In Progress
    const submitted = c.submitted_count || 0; // Submitted + Approved
    const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;

    // Distinct manager count (for the breakdown section header).
    // Sourced from the cycle list API's manager_count aggregation so it's
    // available on page load - independent of whether the user has expanded
    // the section yet (which is what triggers the by-cycle reviews fetch).
    const reviewsArr = safeArr(breakdown?.reviews);
    const managerCount = c.manager_count || 0;

    // Email history count (for the section header pill)
    const historyCount = safeArr(c.emailHistory).length;

    // ── Inline edit state ─────────────────────────────────────────────────
    // editingDates  - bool: is this card in edit mode?
    // editR         - working copy of the reminders array: [{ number, date, has_fired }]
    // Seeded from the cycle's c.reminders on entering edit mode so Cancel can
    // discard local changes cleanly.
    const [editingDates, setEditingDates] = useState(false);
    const [editR, setEditR] = useState([]);
    const [editSaving, setEditSaving] = useState(false);

    const canEditDates = c.status !== "Closed";

    // R1 + R2 only - R3 retired.
    const editWin = cycleWindow(c.year, c.half_num);

    const startEdit = () => {
        const reminders = safeArr(c.reminders);
        const r1 = reminders.find(rm => rm.reminder_number === 1);
        const r2 = reminders.find(rm => rm.reminder_number === 2);
        const seed = [
            { number: 1, date: r1?.reminder_date || c.r1_date || "", has_fired: !!r1?.has_fired },
            { number: 2, date: r2?.reminder_date || c.r2_date || "", has_fired: !!r2?.has_fired },
        ];
        setEditR(seed);
        setEditingDates(true);
    };
    const cancelEdit = () => {
        setEditR([]);
        setEditingDates(false);
    };
    const updateReminderDate = (idx, val) => {
        setEditR(prev => prev.map((r, i) => i === idx ? { ...r, date: val } : r));
    };
    const saveEdit = async () => {
        if (!editR[0]?.date) return showAlert("warning", "Reminder 1 is required");
        if (!editR[1]?.date) return showAlert("warning", "Reminder 2 is required");
        if (editR[0].date.slice(0, 7) !== editWin.reminderMonth) {
            return showAlert("warning", "Reminder 1 out of range",
                `Reminder 1 must fall within ${editWin.reminderMonthName} (the deadline month).`);
        }
        if (editR[1].date.slice(0, 7) !== editWin.reminderMonth) {
            return showAlert("warning", "Reminder 2 out of range",
                `Reminder 2 must fall within ${editWin.reminderMonthName} (the deadline month).`);
        }
        if (editR[1].date <= editR[0].date) {
            return showAlert("warning", "Reminder 2 must be after Reminder 1");
        }
        setEditSaving(true);
        try {
            const body = {
                r1_date: editR[0].date,
                r2_date: editR[1].date,
            };
            const res = await apiFetch(`/api/dspr/cycles/${c.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");
            showAlert("success", "Dates updated");
            setEditingDates(false);
            setEditR([]);
            if (onAfterEdit) await onAfterEdit();
        } catch (e) {
            showAlert("error", "Could not save", e.message);
        } finally {
            setEditSaving(false);
        }
    };

    return (
        <div className={`dspr-cycle-card ${expanded.breakdown || expanded.history ? "expanded" : ""}`}>
            <AlertsHost />
            {/* ── Top row: badge + title + dates + progress block ── */}
            <div className="dspr-cycle-head-new">
                <div className="dspr-cycle-badge">{c.half_label?.split(" ")[0] || "H?"}<div className="dspr-cycle-badge-year">{c.year}</div></div>
                <div className="dspr-cycle-info">
                    <div className="dspr-cycle-title-row">
                        <span className="dspr-cycle-label">{c.half_label}</span>
                        <span className="dspr-pill" style={{
                            background: sc.bg,
                            color: sc.fg,
                            opacity: sc.dim ? 0.8 : 1,
                            boxShadow: sc.glow ? `0 0 0 3px ${sc.dot}22, 0 0 12px ${sc.dot}66` : "none",
                        }}>
                            <span className="dspr-pill-dot" style={{
                                background: sc.dot,
                                boxShadow: sc.glow ? `0 0 6px ${sc.dot}` : "none",
                            }} /> {c.status}
                        </span>
                        <span className="dspr-breakdown-counts">
                            <span className="dspr-bc-ns">{notStarted} Not Started</span>
                            <span className="dspr-bc-sep">·</span>
                            <span className="dspr-bc-pn">{pending} Pending</span>
                            <span className="dspr-bc-sep">·</span>
                            <span className="dspr-bc-sb">{submitted} Submitted</span>
                        </span>
                    </div>

                    {/* ── Single dates row (Read mode) or inline editor (Edit mode) ── */}
                    {editingDates ? (
                        <div className="dspr-dates-row dspr-dates-edit">
                            {/* Start + End + Deadline - always locked, derived from half */}
                            <DateSlot label="Start" value={c.start_date} locked />
                            <DateSlot label="End Date" value={c.end_date} locked />
                            <DateSlot label="Deadline" value={c.submission_deadline || editWin.deadline} locked />
                            {/* R1 + R2 - locked if fired, editable otherwise.
                                Both mandatory, both constrained to the deadline month. */}
                            {editR.map((r, idx) => {
                                const baseLabel = `Reminder ${r.number} *`;
                                return (
                                    <DateSlot
                                        key={idx}
                                        label={r.has_fired ? `${baseLabel} · SENT` : baseLabel}
                                        value={r.date}
                                        locked={r.has_fired}
                                        fired={r.has_fired}
                                        min={`${editWin.reminderMonth}-01`}
                                        max={editWin.deadline}
                                        onChange={val => updateReminderDate(idx, val)}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="dspr-dates-row">
                            <span><strong>Start:</strong> {c.start_date}</span>
                            <span><strong>End:</strong> {c.end_date}</span>
                            {c.submission_deadline && (
                                <span><strong>Deadline:</strong> {c.submission_deadline}</span>
                            )}
                            {safeArr(c.reminders).filter(rm => rm.reminder_number <= 2).map(rm => (
                                <span key={rm.reminder_number}>
                                    <strong>R{rm.reminder_number}:</strong> {rm.reminder_date}
                                    {rm.has_fired && <span className="dspr-reminder-fired-badge">sent</span>}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right column: stats (vertical) + actions (horizontal), side-by-side */}
                <div className="dspr-cycle-progress-block">
                    <div className="dspr-cycle-progress-stats">
                        <div className="dspr-cycle-progress-num">{submitted}/{total}</div>
                        <div className="dspr-cycle-progress-label">Submitted</div>
                        <div className="dspr-cycle-progress-bar">
                            <div className="dspr-cycle-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                    <div className="dspr-cycle-progress-actions">
                        {editingDates ? (
                            <>
                                <button className="dspr-btn dspr-btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving ? "Saving…" : "Save"}</button>
                                <button className="dspr-btn dspr-btn-link" onClick={cancelEdit} disabled={editSaving}>Cancel</button>
                            </>
                        ) : (
                            <>
                                {canEditDates && <button className="dspr-btn dspr-btn-grey" onClick={startEdit}>Edit Dates</button>}
                                {c.status === "Scheduled" && (
                                    activateBlockedReason ? (
                                        // Blocked → no button at all, just the
                                        // amber explanation pill. Saves space
                                        // and removes the dead grey button HR
                                        // can't click anyway. Tight padding
                                        // + flex-item shrink so it sits flush
                                        // next to "Edit Dates" without a gap.
                                        <span style={{
                                            fontSize: 11,
                                            color: "#B45309",
                                            background: "#FFFBEB",
                                            border: "1px solid #FDE68A",
                                            padding: "4px 8px",
                                            borderRadius: 6,
                                            maxWidth: 240,
                                            lineHeight: 1.35,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            flexShrink: 1,
                                        }}>
                                            <span aria-hidden="true">⚠</span>
                                            <span>{activateBlockedReason}</span>
                                        </span>
                                    ) : (
                                        <button
                                            className="dspr-btn dspr-btn-green"
                                            title="Activate this cycle"
                                            onClick={() => onTransition(c.id, "activate", "Activate this cycle? Reviews will be seeded for every allocated employee and Request emails can then go out.")}>
                                            Activate
                                        </button>
                                    )
                                )}
                                {c.status === "Active" && (
                                    <button
                                        className="dspr-btn dspr-btn-grey"
                                        title="Pull in employees hired - or newly given a reporting manager - since this cycle was activated"
                                        onClick={() => onSync(c.id, c.half_label)}>
                                        Sync Employees
                                    </button>
                                )}
                                {c.status === "Active" && <button className="dspr-btn dspr-btn-red" onClick={() => onTransition(c.id, "close", "Close this cycle? Pending reviews stay where they are. You can reopen it later.")}>Close Cycle</button>}
                                {c.status === "Closed" && (
                                    <button
                                        className="dspr-btn dspr-btn-green"
                                        title="Reopen this cycle so it can accept submissions again"
                                        onClick={() => onTransition(c.id, "reopen", `Reopen ${c.half_label}? Employees will be able to submit again until you close it. (Counts against the max-2-active limit.)`)}>
                                        Reopen Cycle
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Collapsible header row - two sections side-by-side ── */}
            <div className="dspr-cycle-tabs">
                <button className={`dspr-cycle-tab ${expanded.breakdown ? "open" : ""}`} onClick={() => onToggleSection(c.id, "breakdown")}>
                    {/* Label + count wrapped together so they stay glued left;
                        the arrow is the second flex child and floats right. */}
                    <span className="dspr-tab-label">
                        Manager &amp; Employee Breakdown
                        {managerCount > 0 && <span className="dspr-tab-count">({managerCount} manager{managerCount !== 1 ? "s" : ""})</span>}
                    </span>
                    <span className="dspr-tab-arrow">{expanded.breakdown ? "▲" : "▼"}</span>
                </button>
                <button className={`dspr-cycle-tab ${expanded.history ? "open" : ""}`} onClick={() => onToggleSection(c.id, "history")}>
                    <span className="dspr-tab-label">
                        Email Trigger History
                        {historyCount > 0 && <span className="dspr-tab-count">({historyCount})</span>}
                    </span>
                    <span className="dspr-tab-arrow">{expanded.history ? "▲" : "▼"}</span>
                </button>
            </div>

            {/* ── Expanded contents ── */}
            {expanded.breakdown && (
                <div className="dspr-cycle-body">
                    {!breakdown ? (
                        <div className="dspr-empty">Loading breakdown…</div>
                    ) : c.status === "Scheduled" ? (
                        <div className="dspr-warn">Cycle is <strong>Scheduled</strong> - activate it to send Request/Reminder mails.</div>
                    ) : (
                        <ManagerBreakdown
                            cycleId={c.id}
                            cycleStatus={c.status}
                            cycleHalfLabel={c.half_label}
                            cycleEndDate={c.end_date}
                            reviews={reviewsArr}
                            onSend={onSend}
                            onDirectSend={onDirectSend}
                            navigate={navigate}
                        />
                    )}
                </div>
            )}

            {expanded.history && (
                <div className="dspr-cycle-body dspr-cycle-history-body">
                    <EmailTriggerHistory history={safeArr(c.emailHistory)} cycleHalfLabel={c.half_label} />
                </div>
            )}
        </div>
    );
}

// ── DateSlot - one labelled date field used in the inline dates editor ──────
// Renders a small uppercase label + a date input. When `locked` is true the
// input is read-only/disabled. When `fired` is true (a reminder that's
// already been sent), the label + input get the green "SENT" styling.
// `onRemove` adds an × button next to the input - only for removable
// reminders (R2/R3 that haven't fired).
function DateSlot({ label, value, locked, fired, onChange, onRemove, min, max }) {
    return (
        <div className={`dspr-date-slot ${locked ? "dspr-date-slot-locked" : ""} ${fired ? "dspr-date-slot-fired" : ""}`}>
            <div className="dspr-date-label">{label}</div>
            <div className="dspr-date-input-wrap">
                <input
                    type="date"
                    value={value || ""}
                    readOnly={locked}
                    disabled={locked}
                    min={min}
                    max={max}
                    onChange={onChange ? (e => onChange(e.target.value)) : undefined}
                    onClick={locked ? undefined : (e => e.currentTarget.showPicker?.())}
                />
                {onRemove && (
                    <button type="button" className="dspr-date-remove" onClick={onRemove} title="Remove this reminder">×</button>
                )}
            </div>
        </div>
    );
}

// ── ManagerBreakdown - per-manager block with pills + buttons + emp chips ────
function ManagerBreakdown({ cycleId, cycleStatus, cycleHalfLabel, cycleEndDate, reviews, onSend, onDirectSend, navigate }) {
    // Group reviews by manager_id. manager_id is NOT NULL by schema, so a single
    // bucket per RM.
    const grouped = useMemo(() => {
        const map = new Map();
        safeArr(reviews).forEach(r => {
            if (!map.has(r.manager_id)) {
                map.set(r.manager_id, {
                    manager_id: r.manager_id,
                    manager_name: `${r.manager_first_name || ""} ${r.manager_last_name || ""}`.trim() || r.manager_id,
                    manager_email: r.manager_email,
                    rows: [],
                });
            }
            map.get(r.manager_id).rows.push(r);
        });
        return [...map.values()];
    }, [reviews]);

    const [previewBatch, setPreviewBatch] = useState(null); // { manager, act, label, employees }
    const [emailLogOpen, setEmailLogOpen] = useState({}); // managerId → bool

    if (grouped.length === 0) {
        return <div className="dspr-empty">No allocated employees in this cycle yet.</div>;
    }

    return (
        <div className="dspr-breakdown">
            {grouped.map(g => {
                const total = g.rows.length;
                // Per-step counters: N done / M total. M = total in group.
                // "done" semantics:
                //   Req: status !== 'Not Started' (i.e. request already fired)
                //   R1/R2/R3: any email_log with email_type 'reminderN'
                const reqDone = g.rows.filter(r => r.status !== "Not Started").length;
                const r1Done = g.rows.filter(r => safeArr(r.email_logs).some(l => l.email_type === "reminder1")).length;
                const r2Done = g.rows.filter(r => safeArr(r.email_logs).some(l => l.email_type === "reminder2")).length;
                const reqPending = total - reqDone;
                // R1 eligibility: in-flight + R1 not yet sent. R1 cascade
                // upstream is Request Review (covered elsewhere).
                const r1Pending = g.rows.filter(r =>
                    ["Initiated", "In Progress"].includes(r.status)
                    && !safeArr(r.email_logs).some(l => l.email_type === "reminder1")
                ).length;
                // R2 eligibility: in-flight + R1 ALREADY sent + R2 not yet
                // sent. An employee who never got R1 can't be the target of R2.
                const r2Pending = g.rows.filter(r =>
                    ["Initiated", "In Progress"].includes(r.status)
                    && safeArr(r.email_logs).some(l => l.email_type === "reminder1")
                    && !safeArr(r.email_logs).some(l => l.email_type === "reminder2")
                ).length;

                // Latest-sent timestamps per step - used when the step is fully
                // done to render a "✓ <step> · <when>" pill in place of the button.
                const latestAtForType = (type) => {
                    let latest = null;
                    g.rows.forEach(r => {
                        safeArr(r.email_logs).filter(l => l.email_type === type).forEach(l => {
                            if (!latest || l.sent_at > latest) latest = l.sent_at;
                        });
                    });
                    return latest;
                };
                const reqLatestAt = latestAtForType("request");
                const reminderLatestAt = [
                    latestAtForType("reminder1"),
                    latestAtForType("reminder2"),
                ];

                // Plain computation per render - useMemo can't live inside a
                // loop (rules of hooks). Cheap enough for typical cycle sizes.
                const emailLogEntries = (() => {
                    const entries = [];
                    g.rows.forEach(r => {
                        safeArr(r.email_logs).forEach(l => {
                            entries.push({
                                emp_id: r.employee_id,
                                emp_name: `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim(),
                                emp_mail: r.emp_mail_id,
                                email_type: l.email_type,
                                sent_at: l.sent_at,
                            });
                        });
                    });
                    return entries.sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));
                })();

                const isLogOpen = !!emailLogOpen[g.manager_id];
                const canSend = cycleStatus === "Active";

                return (
                    <div key={g.manager_id} className="dspr-mgr-block">
                        {/* Top row - identity on the left, pills + actions on the right.
                            Matches the dEpr "Ashwin K" stakeholder row layout. */}
                        <div className="dspr-mgr-top">
                            {/* Left: manager name, id, email */}
                            <div className="dspr-mgr-identity">
                                <div className="dspr-mgr-name-row">
                                    {/* Same hash-hue used for employee avatars + navbar */}
                                    <span className="dspr-mgr-dot"
                                          style={{ background: profileColorFromName((g.manager_name || "").split(" ")[0]) }} />
                                    <strong>{g.manager_name}</strong>
                                    <span className="dspr-mgr-id">{g.manager_id}</span>
                                </div>
                                {g.manager_email && <div className="dspr-mgr-email">{g.manager_email}</div>}
                            </div>

                            {/* Right: emp count + 4 status pills + eye + Request Review on top row;
                                Send Reminder 1/2/3 stacked vertically below */}
                            <div className="dspr-mgr-right">
                                <div className="dspr-mgr-right-top">
                                    <span className="dspr-mgr-emp-count">{total} emp{total !== 1 ? "s" : ""}</span>
                                    <StepPill type="req" done={reqDone} total={total} />
                                    <StepPill type="r1" done={r1Done} total={total} />
                                    <StepPill type="r2" done={r2Done} total={total} />
                                    {/* R3 step pill retired */}
                                    {reqDone === total && total > 0 ? (
                                        <span className="dspr-action-done">✓ Requested · {fmtDateTimeShort(reqLatestAt)}</span>
                                    ) : (
                                        <>
                                            <EyeButton
                                                onClick={() => setPreviewBatch({
                                                    cycleId,
                                                    managerId: g.manager_id,
                                                    manager: g,
                                                    act: "request",
                                                    label: "Request review",
                                                    cycleHalfLabel,
                                                    cycleEndDate,
                                                    employees: g.rows.filter(r => r.status === "Not Started"),
                                                })}
                                                disabled={reqPending === 0}
                                            />
                                            <button className="dspr-batch-btn dspr-batch-req"
                                                disabled={!canSend || reqPending === 0}
                                                onClick={() => onSend(cycleId, g.manager_id, "request", "Request")}>
                                                Request Review <span className="dspr-batch-pending">{reqPending}</span>
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Send Reminder 1 / 2 stacked vertically, each with an eye
                                    preview alongside (matches the Review Management page).
                                    Cascade: R2 stays disabled until at least one R1 has
                                    fired in this manager's group - same gating logic the
                                    per-employee Review Management cards use. */}
                                <div className="dspr-mgr-reminders">
                                    {[1, 2].map(n => {
                                        const done = [r1Done, r2Done][n - 1];
                                        const pending = [r1Pending, r2Pending][n - 1];
                                        const latestAt = reminderLatestAt[n - 1];
                                        if (done === total && total > 0) {
                                            return (
                                                <span key={n} className="dspr-action-done">
                                                    ✓ Reminder {n} · {fmtDateTimeShort(latestAt)}
                                                </span>
                                            );
                                        }
                                        // Cascade gate: R2 requires R1 to have fired for at
                                        // least one employee in this group. R1 has no
                                        // upstream gate beyond Request Review (covered by
                                        // reqDone - see line above's "if Request not done"
                                        // block; R1 button intentionally still appears so
                                        // HR sees the queue, just disabled).
                                        const cascadeBlocked = n === 2 && r1Done === 0;
                                        const disabled = !canSend || pending === 0 || cascadeBlocked;
                                        // For R2 the preview must mirror the cascade
                                        // gate: only employees who already received R1
                                        // can be queued for R2.
                                        const previewEmployees = g.rows.filter(r => {
                                            if (!["Initiated", "In Progress"].includes(r.status)) return false;
                                            if (safeArr(r.email_logs).some(l => l.email_type === `reminder${n}`)) return false;
                                            if (n === 2 && !safeArr(r.email_logs).some(l => l.email_type === "reminder1")) return false;
                                            return true;
                                        });
                                        const title = cascadeBlocked
                                            ? "Send Reminder 1 first"
                                            : (pending === 0 ? "All employees already received this reminder" : `Send Reminder ${n} to ${pending} employee${pending === 1 ? "" : "s"}`);
                                        return (
                                            <div key={n} className="dspr-mgr-reminder-row">
                                                <EyeButton
                                                    onClick={() => setPreviewBatch({
                                                        cycleId,
                                                        managerId: g.manager_id,
                                                        manager: g,
                                                        act: `r${n}`,
                                                        label: `Reminder ${n}`,
                                                        cycleHalfLabel,
                                                        cycleEndDate,
                                                        employees: previewEmployees,
                                                    })}
                                                    disabled={previewEmployees.length === 0 || cascadeBlocked}
                                                />
                                                <button className="dspr-batch-btn dspr-batch-reminder"
                                                    disabled={disabled}
                                                    title={title}
                                                    onClick={() => onSend(cycleId, g.manager_id, `r${n}`, `Reminder ${n}`)}>
                                                    Send Reminder {n}
                                                    {pending > 0 && <span className="dspr-batch-pending">{pending}</span>}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Employee chips - smaller, fixed-width, flow horizontally */}
                        <div className="dspr-emp-grid">
                            {g.rows.map(r => {
                                const status = REVIEW_STATUS_COLORS[r.status] || REVIEW_STATUS_COLORS["Not Started"];
                                const initials = `${(r.emp_first_name || "?").charAt(0)}${(r.emp_last_name || "").charAt(0)}`.toUpperCase();
                                const fullName = `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim();
                                // Same hash-based hue the left navbar uses (Employee_server.js
                                // computes the same value for the logged-in user). Inline style
                                // overrides the .dspr-emp-avatar gradient.
                                const avatarBg = profileColorFromName(r.emp_first_name);
                                return (
                                    <div key={r.id} className="dspr-emp-card" title={fullName}>
                                        <div className="dspr-emp-avatar"
                                             style={{ background: avatarBg, color: "#fff" }}>
                                            {initials}
                                        </div>
                                        <div className="dspr-emp-info">
                                            {/* Row 1: Name (left) | DZIND code (right) */}
                                            <div className="dspr-emp-row">
                                                <span className="dspr-emp-name">{fullName}</span>
                                                <span className="dspr-emp-id-code">{r.employee_id}</span>
                                            </div>
                                            {/* Row 2: Job position (left) | Status pill (right) */}
                                            <div className="dspr-emp-row">
                                                <span className="dspr-emp-title">{r.job_position || "-"}</span>
                                                <span className="dspr-emp-status-pill" style={{ background: status.bg, color: status.fg }}>
                                                    <span className="dspr-emp-status-dot" style={{ background: status.fg }} />
                                                    {r.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Per-manager Email Log collapsible */}
                        <div className="dspr-mgr-log">
                            <button className="dspr-mgr-log-toggle" onClick={() => setEmailLogOpen(prev => ({ ...prev, [g.manager_id]: !prev[g.manager_id] }))}>
                                📋 Email Log {emailLogEntries.length > 0 && `(${emailLogEntries.length})`} {isLogOpen ? "▲" : "▼"}
                            </button>
                            {isLogOpen && (
                                emailLogEntries.length === 0 ? (
                                    <div className="dspr-empty" style={{ padding: "12px 0", fontSize: 12 }}>No emails sent yet for this manager's employees.</div>
                                ) : (
                                    <table className="dspr-mgr-log-table">
                                        <thead><tr><th>Employee</th><th>Email Type</th><th>Sent at</th></tr></thead>
                                        <tbody>
                                            {emailLogEntries.map((e, i) => (
                                                <tr key={i}>
                                                    <td>{e.emp_name} <span className="dspr-emp-meta">{e.emp_id}</span></td>
                                                    <td>{EMAIL_TYPE_META[e.email_type]?.label || e.email_type}</td>
                                                    <td>{fmtDateTimeIST(e.sent_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                        </div>
                    </div>
                );
            })}
            {previewBatch && (
                <BatchPreviewModal
                    batch={previewBatch}
                    onClose={() => setPreviewBatch(null)}
                    onDirectSend={onDirectSend}
                />
            )}
        </div>
    );
}

// Step pill - green/amber/purple/red dot + "N/M" + solid-fill when complete
function StepPill({ type, done, total }) {
    const meta = {
        req: { dot: "#10b981", label: "Req" },
        r1:  { dot: "#f59e0b", label: "R1" },
        r2:  { dot: "#8b5cf6", label: "R2" },
    }[type];
    const complete = total > 0 && done === total;
    return (
        <span className={`dspr-step-pill ${complete ? "complete" : ""}`} style={complete ? { background: meta.dot, color: "#fff", borderColor: meta.dot } : {}}>
            <span className="dspr-step-dot" style={{ background: complete ? "#fff" : meta.dot }} />
            {meta.label} {done}/{total}
        </span>
    );
}

function EyeButton({ onClick, disabled }) {
    return (
        <button className="dspr-eye-btn" onClick={onClick} disabled={disabled} title="Preview this batch">
            <FaRegEye />
        </button>
    );
}

// ── EmailTriggerHistory ─────────────────────────────────────────────────────
//
// Renders a vertical timeline. One row per email_type, fixed order:
//   request → reminder1 → reminder2 → reminder3 → confirmation
// Empty cells (types not yet triggered) render dimmed.
function EmailTriggerHistory({ history, cycleHalfLabel }) {
    const byType = useMemo(() => {
        const m = new Map();
        safeArr(history).forEach(h => m.set(h.type, h));
        return m;
    }, [history]);

    const triggered = EMAIL_TYPE_ORDER.filter(t => byType.has(t));
    if (triggered.length === 0) {
        return <div className="dspr-empty">No emails triggered yet for this cycle.</div>;
    }

    return (
        <div className="dspr-timeline">
            <div className="dspr-timeline-head">Email Activity Timeline - {cycleHalfLabel}</div>
            {EMAIL_TYPE_ORDER.map(t => {
                const item = byType.get(t);
                const meta = EMAIL_TYPE_META[t];
                const muted = !item;
                return (
                    <div key={t} className={`dspr-timeline-row ${muted ? "muted" : ""}`}>
                        <div className="dspr-timeline-dot" style={{ background: muted ? "#cbd5e1" : meta.dot }} />
                        <div className="dspr-timeline-body">
                            <div className="dspr-timeline-label">{meta.label}</div>
                            <div className="dspr-timeline-notes">
                                {item ? meta.notesFn(item.recipient_count) : "Not yet triggered"}
                            </div>
                        </div>
                        {item && (
                            <>
                                <div className="dspr-timeline-pill">{item.recipient_count} Recipient{item.recipient_count !== 1 ? "s" : ""}</div>
                                <div className="dspr-timeline-when">{fmtDateTimeIST(item.latest_at)}</div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Batch preview modal - dEpr-style email preview ──────────────────────────
//
// Layout (top → bottom):
//   • Header bar - "Email Preview" + [⟲ live] pill + {label · half · N emps}
//   • Header card - To / CC / Sub (read-only meta)
//   • Body iframe - full buildShell HTML rendered server-side via
//                     POST /api/dspr/email-templates/preview
//   • Recipients - collapsible table (closed by default)
//   • CC note - informational
//   • Footer - Cancel · Send to N Employees
//
// On open, fetches the live template via the preview route. The fetched HTML
// includes the chrome (banner/footer/styled button) so HR sees an identical
// render to what the recipient will see in their inbox.
//
// Send button calls onDirectSend(cycleId, managerId, act, label) - the
// no-Swal-confirm path - because the preview itself is the confirmation step.
const ACT_TO_TEMPLATE_TYPE = {
    request: "request_review",
    r1: "reminder_1",
    r2: "reminder_2",
};

function BatchPreviewModal({ batch, onClose, onDirectSend }) {
    const [template, setTemplate] = useState(null);   // { subject, html, alwaysCc }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Default to OPEN so HR sees exactly which employees will get the email
    // without an extra click. Especially useful on reminder previews where the
    // batch is a subset of the manager's group.
    const [recipientsOpen, setRecipientsOpen] = useState(true);
    const [sending, setSending] = useState(false);

    const type = ACT_TO_TEMPLATE_TYPE[batch.act];
    const N = safeArr(batch.employees).length;

    // Fetch the rendered preview on mount. Only fires once per modal open.
    useEffect(() => {
        (async () => {
            setLoading(true); setError(null);
            try {
                // Backend computes per-type deadline (request → R1, R1 → R2,
                // R2 → R3, R3 → end+90) from the cycle row it fetches via
                // cycle_id. We pass the first employee in the batch as a
                // representative example so the preview body reads with a
                // real name + designation - every recipient gets the same
                // body, just with their own substitution at send time.
                const firstEmp = safeArr(batch.employees)[0] || {};
                const empName = `${firstEmp.emp_first_name || ""} ${firstEmp.emp_last_name || ""}`.trim();
                // Build the REAL review URL for the first employee in the
                // batch so right-click → "Copy link address" copies a real
                // working URL. Button is target="_blank" - left-click opens
                // a new tab instead of breaking the preview iframe.
                const realLink = firstEmp.id
                    ? `${window.location.origin}/dspr/review?r=${encodeURIComponent(firstEmp.id)}`
                    : "<Personalized self-review link>";
                const vars = {
                    employee_name: empName || "<Recipient Name>",
                    employee_id:   firstEmp.employee_id || "",
                    job_position:  firstEmp.job_position || "<Designation>",
                    review_link:   realLink,
                };
                const res = await apiFetch("/api/dspr/email-templates/preview", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ type, vars, cycle_id: batch.cycleId }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || "Couldn't load preview");
                setTemplate(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSend = async () => {
        if (!onDirectSend) return;
        setSending(true);
        try {
            // Pass the whole batch object so the parent can decide between
            // bulk-by-manager (Cycle Scheduler) vs single-emp (Review Mgmt)
            // based on which keys are present.
            await onDirectSend(batch);
            onClose();
        } catch {
            // doSend* already showed an error Swal; stay in modal so
            // HR can read the failure context and decide whether to retry.
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal dspr-preview-modal" onClick={e => e.stopPropagation()}>
                {/* Header bar */}
                <div className="dspr-modal-head dspr-preview-head">
                    <div className="dspr-preview-head-text">
                        <div className="dspr-preview-head-title">
                            Email Preview
                            <span className="dspr-preview-live-pill"
                                title="Preview reads from saved template. Cache TTL 30s - if you edited the template just now, wait or restart server for immediate refresh.">
                                ⟲ live
                            </span>
                        </div>
                        <div className="dspr-preview-head-subtitle">
                            {batch.label} · {batch.cycleHalfLabel} · {N} Employee{N !== 1 ? "s" : ""} (separate emails)
                        </div>
                    </div>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="dspr-modal-body">
                    {/* Header card - To / CC / Sub */}
                    <div className="dspr-preview-header-card">
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">To:</span>
                            <span className="dspr-preview-value">&lt;Recipient Name&gt;</span>
                        </div>
                        <div className="dspr-preview-meta-sub">(1 email per employee, sent separately)</div>
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">CC:</span>
                            <span className="dspr-preview-value">
                                {(template?.alwaysCc || ["admin@dolluzcorp.com", "anandthshoban@dolluzcorp.com", "hr@dolluzcorp.com"]).join(", ")}
                            </span>
                        </div>
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">Sub:</span>
                            <span className="dspr-preview-value dspr-preview-subject-text">
                                {loading ? "Loading…" : error ? "-" : template?.subject || "-"}
                            </span>
                        </div>
                    </div>

                    {/* Body iframe */}
                    <div className="dspr-preview-iframe-wrap">
                        {loading ? (
                            <div className="dspr-empty" style={{ padding: 30 }}>Loading preview…</div>
                        ) : error ? (
                            <div className="dspr-tpl-error">⚠ {error}</div>
                        ) : (
                            <iframe
                                className="dspr-preview-iframe"
                                srcDoc={template.html}
                                title="Email Preview"
                                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                            />
                        )}
                    </div>

                    {/* Recipients collapsible - open by default. Header carries
                        a comma-separated name preview so HR sees who's in the
                        batch even without expanding the table. */}
                    <div className="dspr-preview-recipients">
                        <button
                            type="button"
                            className="dspr-preview-rec-toggle"
                            onClick={() => setRecipientsOpen(o => !o)}>
                            <span>{recipientsOpen ? "▲" : "▼"} Recipients ({N} employee{N !== 1 ? "s" : ""} will receive personalized emails)</span>
                        </button>
                        {N > 0 && (
                            <div className="dspr-preview-rec-names" style={{
                                fontSize: 12, color: "#475569", padding: "4px 12px 8px",
                                lineHeight: 1.5,
                            }}>
                                {safeArr(batch.employees)
                                    .map(r => `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim())
                                    .filter(Boolean)
                                    .join(", ")}
                            </div>
                        )}
                        {recipientsOpen && (
                            <table className="dspr-preview-rec-table">
                                <thead>
                                    <tr><th>Emp ID</th><th>Name</th><th>Email</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {N === 0 ? (
                                        <tr><td colSpan={4} className="dspr-empty">No eligible employees in this batch.</td></tr>
                                    ) : safeArr(batch.employees).map(r => {
                                        const status = REVIEW_STATUS_COLORS[r.status] || REVIEW_STATUS_COLORS["Not Started"];
                                        return (
                                            <tr key={r.id}>
                                                <td>{r.employee_id}</td>
                                                <td>{r.emp_first_name} {r.emp_last_name}</td>
                                                <td>{r.emp_mail_id}</td>
                                                <td><span className="dspr-pill" style={{ background: status.bg, color: status.fg }}>{r.status}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="dspr-preview-cc-note">
                        ℹ All outgoing emails automatically include the CC list above.
                    </div>
                </div>

                {/* Footer */}
                <div className="dspr-modal-foot">
                    <button className="dspr-btn dspr-btn-link" onClick={onClose} disabled={sending}>Cancel</button>
                    <button
                        className="dspr-btn dspr-btn-primary"
                        onClick={handleSend}
                        disabled={sending || N === 0 || loading || !!error}>
                        {sending
                            ? "Sending…"
                            : (N === 1 && batch.singleEmployeeName
                                ? `Send to ${batch.singleEmployeeName}`
                                : `Send to ${N} Employee${N !== 1 ? "s" : ""}`)}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Half-cycle window helper (mirrors backend defaultDates) ─────────────────
function cycleWindow(year, halfNum) {
    const y = Number(year);
    if (halfNum === 1) {
        return {
            start:     `${y}-01-01`,
            end:       `${y}-06-30`,
            deadline:  `${y}-07-31`,
            // The month reminders must sit in (YYYY-MM)
            reminderMonth: `${y}-07`,
            reminderMonthName: `July ${y}`,
        };
    }
    return {
        start:     `${y}-07-01`,
        end:       `${y}-12-31`,
        deadline:  `${y + 1}-01-31`,
        reminderMonth: `${y + 1}-01`,
        reminderMonthName: `January ${y + 1}`,
    };
}

// fmtDateLong now lives in src/utils/dSpr_DateFormat.js - imported at top.

// ── New Cycle Modal ─────────────────────────────────────────────────────────
//
// Both R1 and R2 are mandatory and MUST sit inside the deadline calendar
// month (Jul for H1, next-year Jan for H2).
function NewCycleModal({ onClose, onCreated, existingCycles = [] }) {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [halfNum, setHalfNum] = useState(today.getMonth() < 6 ? 1 : 2);
    const [r1Date, setR1Date] = useState("");
    const [r2Date, setR2Date] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { showAlert, AlertsHost } = useDsprAlerts();

    const win = cycleWindow(year, halfNum);

    // Build a Set of year-half combos that already exist so the modal can
    // block creation client-side instead of relying on the 409 server response.
    const takenSet = useMemo(
        () => new Set(safeArr(existingCycles).map(c => `${c.year}-${c.half_num}`)),
        [existingCycles]
    );
    const yearNum = Number(year);
    const halfTaken = takenSet.has(`${yearNum}-${halfNum}`);
    const otherHalfTaken = takenSet.has(`${yearNum}-${halfNum === 1 ? 2 : 1}`);
    const otherHalfLabel = `H${halfNum === 1 ? 2 : 1} ${yearNum}`;

    // Re-seed reminder defaults whenever year/half changes so the dates land
    // inside the deadline month.
    useEffect(() => {
        const w = cycleWindow(year, halfNum);
        setR1Date(`${w.reminderMonth}-10`);
        setR2Date(`${w.reminderMonth}-25`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, halfNum]);

    const submit = async (e) => {
        e.preventDefault();
        if (halfTaken) {
            return showAlert("warning", "Cycle already exists",
                `H${halfNum} ${yearNum} has already been created. Pick a different half or year.`);
        }
        if (!r1Date) return showAlert("warning", "Reminder 1 is required");
        if (!r2Date) return showAlert("warning", "Reminder 2 is required");
        if (r1Date.slice(0, 7) !== win.reminderMonth) {
            return showAlert("warning", "Reminder 1 out of range",
                `Reminder 1 must fall within ${win.reminderMonthName} (the deadline month).`);
        }
        if (r2Date.slice(0, 7) !== win.reminderMonth) {
            return showAlert("warning", "Reminder 2 out of range",
                `Reminder 2 must fall within ${win.reminderMonthName} (the deadline month).`);
        }
        if (r2Date <= r1Date) {
            return showAlert("warning", "Reminder 2 must be after Reminder 1");
        }
        if (r1Date > win.deadline || r2Date > win.deadline) {
            return showAlert("warning", "Reminder after deadline",
                `Reminders cannot be after the deadline (${fmtDateLong(win.deadline)}).`);
        }
        setSubmitting(true);
        try {
            const body = {
                year: Number(year), half_num: Number(halfNum),
                r1_date: r1Date,
                r2_date: r2Date,
            };
            const res = await apiFetch("/api/dspr/cycles", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Create failed");
            // Note: the new activate-flow doesn't seed reviews at create time
            // - the success message reflects that. Activation seeds them.
            showAlert("success", "Cycle created",
                `Created ${data.cycle_id} as Scheduled. Activate it to seed review rows and start sending Request emails.`);
            onCreated();
        } catch (e) {
            showAlert("error", "Could not create cycle", e.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Force the date pickers into the deadline month so HR can't even pick
    // a date outside the valid window.
    const reminderMin = `${win.reminderMonth}-01`;
    const reminderMax = win.deadline;

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal" onClick={(e) => e.stopPropagation()}>
                <AlertsHost />
                <div className="dspr-modal-head">
                    <span>New Cycle</span>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={submit} className="dspr-modal-body">
                    <div className="dspr-form-row">
                        <div className="dspr-form-group">
                            <label>Year *</label>
                            <input type="number" min="2024" max="2099" value={year} onChange={e => setYear(e.target.value)} required />
                        </div>
                        <div className="dspr-form-group">
                            <label>Half *</label>
                            <div className="dspr-half-picker">
                                <button type="button"
                                    className={halfNum === 1 ? "active" : ""}
                                    disabled={takenSet.has(`${yearNum}-1`)}
                                    title={takenSet.has(`${yearNum}-1`) ? `H1 ${yearNum} already exists` : ""}
                                    onClick={() => setHalfNum(1)}>
                                    H1 (Jan-Jun)
                                </button>
                                <button type="button"
                                    className={halfNum === 2 ? "active" : ""}
                                    disabled={takenSet.has(`${yearNum}-2`)}
                                    title={takenSet.has(`${yearNum}-2`) ? `H2 ${yearNum} already exists` : ""}
                                    onClick={() => setHalfNum(2)}>
                                    H2 (Jul-Dec)
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Auto-derived dates summary so HR knows exactly what
                        windows + deadline the cycle will use. Last line flips
                        to a red "Already exists" notice when the selected
                        year + half has been created before. */}
                    <div className="dspr-form-hint" style={{ marginBottom: 14, lineHeight: 1.6 }}>
                        <strong>Cycle window:</strong> {fmtDateLong(win.start)} - {fmtDateLong(win.end)}<br />
                        <strong>Submission deadline:</strong> {fmtDateLong(win.deadline)}<br />
                        <strong>Reminders must fall in:</strong> {win.reminderMonthName}
                        {halfTaken && (
                            <>
                                <br />
                                <span style={{ color: "#b4453a", fontWeight: 700 }}>
                                    Already exists - pick a different year or half.
                                </span>
                            </>
                        )}
                    </div>

                    {/* R1 + R2 - both mandatory, both constrained to the deadline month */}
                    <div className="dspr-modal-reminders">
                        <div className="dspr-modal-reminder-row">
                            <label>Reminder 1 *</label>
                            <div className="dspr-input-with-x">
                                <input type="date"
                                    value={r1Date}
                                    min={reminderMin}
                                    max={reminderMax}
                                    onChange={e => setR1Date(e.target.value)}
                                    onClick={(e) => e.currentTarget.showPicker?.()}
                                    required />
                            </div>
                        </div>
                        <div className="dspr-modal-reminder-row">
                            <label>Reminder 2 *</label>
                            <div className="dspr-input-with-x">
                                <input type="date"
                                    value={r2Date}
                                    min={reminderMin}
                                    max={reminderMax}
                                    onChange={e => setR2Date(e.target.value)}
                                    onClick={(e) => e.currentTarget.showPicker?.()}
                                    required />
                            </div>
                        </div>
                    </div>

                    <div className="dspr-form-hint">
                        When a reminder date is hit, the system sends an alert email to admin
                        and adds a notification. The system never auto-sends to employees -
                        click Send Reminder 1/2 below to dispatch manually.
                    </div>
                    <div className="dspr-modal-foot">
                        <button type="button" className="dspr-btn dspr-btn-grey" onClick={onClose} disabled={submitting}>Cancel</button>
                        <button type="submit" className="dspr-btn dspr-btn-primary"
                            disabled={submitting || halfTaken}
                            title={halfTaken ? `H${halfNum} ${yearNum} already exists` : ""}>
                            {submitting ? "Creating…" : "Create Cycle"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// (Edit Dates is inline - see CycleCard's editingDates state. No modal.)

// ════════════════════════════════════════════════════════════════════════════
// CLIENT / PROJECT OPTIONS MODAL - CRUD for the self-review form's dropdown
// ════════════════════════════════════════════════════════════════════════════
function ClientProjectOptionsModal({ onClose }) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [busy, setBusy] = useState(false);
    const { showAlert, showConfirm, AlertsHost } = useDsprAlerts();

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dspr/client-project-options", { credentials: "include" });
            const data = await res.json();
            if (res.ok) setOptions(Array.isArray(data) ? data : []);
        } catch (e) {
            showAlert("error", "Couldn't load options", e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        const trimmed = newName.trim();
        if (!trimmed) { showAlert("warning", "Enter a name"); return; }
        setBusy(true);
        try {
            const res = await apiFetch("/api/dspr/client-project-options", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: trimmed }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't add option");
            setNewName("");
            await load();
        } catch (e) {
            showAlert("error", "Add failed", e.message);
        } finally { setBusy(false); }
    };

    const startEdit = (opt) => { setEditingId(opt.id); setEditingName(opt.name); };
    const cancelEdit = () => { setEditingId(null); setEditingName(""); };
    const saveEdit = async () => {
        const trimmed = editingName.trim();
        if (!trimmed) { showAlert("warning", "Name can't be empty"); return; }
        setBusy(true);
        try {
            const res = await apiFetch(`/api/dspr/client-project-options/${editingId}`, {
                method: "PUT", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: trimmed }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't update option");
            cancelEdit();
            await load();
        } catch (e) {
            showAlert("error", "Update failed", e.message);
        } finally { setBusy(false); }
    };

    const remove = async (opt) => {
        const ok = await showConfirm({
            title: `Delete "${opt.name}"?`,
            body: "Reviews that already saved this name keep it - deletion only removes it from future picks.",
            confirmLabel: "Delete",
            danger: true,
            icon: "🗑️",  // This is an actual delete - opt-in to the trash icon.
        });
        if (!ok) return;
        setBusy(true);
        try {
            const res = await apiFetch(`/api/dspr/client-project-options/${opt.id}`, {
                method: "DELETE", credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't delete option");
            await load();
        } catch (e) {
            showAlert("error", "Delete failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal dspr-cpo-modal" onClick={e => e.stopPropagation()}>
                <AlertsHost />
                <div className="dspr-modal-head">
                    <h3>Manage client / project options</h3>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="dspr-modal-body">
                    <p className="dspr-cpo-hint">
                        These options appear in the dropdown on the self-review form. <strong>"Other"</strong> is
                        built-in and can't be removed - employees who pick it will see a free-text input.
                    </p>

                    {/* Add new */}
                    <div className="dspr-cpo-add-row">
                        <input type="text" maxLength={80}
                            placeholder="Add a new option (e.g. ClientX)"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") add(); }}
                            disabled={busy}
                        />
                        <button type="button" className="dspr-btn-new"
                            onClick={add}
                            disabled={busy || !newName.trim()}>
                            + Add
                        </button>
                    </div>

                    {/* List */}
                    {loading ? (
                        <div className="dspr-empty">Loading…</div>
                    ) : (
                        <div className="dspr-cpo-list">
                            {options.map(opt => (
                                <div key={opt.id || "_other"} className={`dspr-cpo-row ${opt.builtin ? "builtin" : ""}`}>
                                    {/* Both must be non-null - built-in "Other" has id=null and
                                        would otherwise match the initial editingId=null state and
                                        render a phantom edit form on first open. */}
                                    {editingId != null && editingId === opt.id ? (
                                        <>
                                            <input type="text" maxLength={80}
                                                value={editingName}
                                                onChange={e => setEditingName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === "Enter") saveEdit();
                                                    if (e.key === "Escape") cancelEdit();
                                                }}
                                                autoFocus
                                                disabled={busy}
                                            />
                                            <button className="dspr-cpo-btn save" onClick={saveEdit} disabled={busy}>Save</button>
                                            <button className="dspr-cpo-btn cancel" onClick={cancelEdit} disabled={busy}>Cancel</button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="dspr-cpo-name">{opt.name}</span>
                                            {opt.builtin ? (
                                                <span className="dspr-cpo-builtin-tag">built-in</span>
                                            ) : (
                                                <>
                                                    <button className="dspr-cpo-btn edit"
                                                        onClick={() => startEdit(opt)} disabled={busy}>Edit</button>
                                                    <button className="dspr-cpo-btn delete"
                                                        onClick={() => remove(opt)} disabled={busy}>Delete</button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="dspr-modal-foot">
                    <button type="button" className="dspr-btn dspr-btn-grey" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default DSprCycleScheduler;
